import { html, css, LitElement, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import {
  getCurrentLocation,
  getRouteOptionsAndParams,
  navigateTo,
  getCurrentPath,
  isActivationKey,
} from '..';
import '@ui5/webcomponents/dist/DateRangePicker';
// eslint-disable-next-line import/extensions
import { i18nMixin } from '../../decorators';
import { Mix } from '../../common';
import { getCurrentApi } from '../../api';
import nls from '../../locales/index';
import styles from './styles.css?inline';
import { buildStationUrl } from './switchStation';
import { WiskiStation } from '../../defs/WiskiTypes';

// @ts-expect-error
@customElement('ww-river-navigation')
export default class RiverNavigation extends Mix(LitElement, [
  i18nMixin,
  { nls },
]) {
  static styles = css`
    ${unsafeCSS(styles)}
  `;

  @property()
  nextStation;

  @property()
  prevStation;

  @property()
  currStation;

  @property()
  stationId;

  @property()
  parameterLabel;

  @property()
  layerName;

  @property()
  sortedStations;

  api = getCurrentApi();

  render() {
    return this.layerName
      ? html` <div class="river-navigation">
          <ki-icon-btn
            class="ripple"
            icon="ki ki-angle-left"
            title="${this.i18n.t('down_stream')}"
            tabindex="0"
            @click="${() => this.getNextPrevStation('prev')}"
            @keydown="${e => {
              if (isActivationKey(e)) this.getNextPrevStation('prev');
            }}"
          ></ki-icon-btn>
          <ki-icon-btn
            class="ripple"
            icon="ki ki-angle-right"
            title="${this.i18n.t('up_stream')}"
            tabindex="0"
            @click="${() => this.getNextPrevStation('next')}"
            @keydown="${e => {
              if (isActivationKey(e)) this.getNextPrevStation('next');
            }}"
          ></ki-icon-btn>
        </div>`
      : '';
  }

  async firstUpdated() {
    this.fetchLayerData();
  }

  async updated(changedProps) {
    if (changedProps.has('stationId')) {
      const params = getRouteOptionsAndParams(getCurrentLocation(), [
        'layerName',
        'stationId',
        'stationLabel',
      ]);
      this.layerName = params.layerName;
      this.stationId = params.stationId;
      this.stationLabel = params.stationLabel;
      this.parameterLabel = params.options.parameter_label;
    }
  }

  async getNextPrevStation(sequence: String | null) {
    await this.fetchLayerData;

    const stationIndex = this.sortedStations.findIndex(
      station => station.station_id === this.stationId,
    );
    if (stationIndex !== -1) {
      this.currStation = this.sortedStations[stationIndex];
      this.prevStation =
        stationIndex > 0 ? this.sortedStations[stationIndex - 1] : null;
      this.nextStation =
        stationIndex < this.sortedStations.length - 1
          ? this.sortedStations[stationIndex + 1]
          : null;
    }
    if (sequence === 'next') {
      this.switchStation(this.nextStation);
    } else if (sequence === 'prev') {
      this.switchStation(this.prevStation);
    }
    this.requestUpdate();
  }

  async fetchLayerData() {
    const params = getRouteOptionsAndParams(getCurrentLocation(), [
      'layerName',
      'stationId',
      'stationLabel',
    ]);
    this.layerName = params.layerName;
    this.stationId = params.stationId;
    this.stationLabel = params.stationLabel;
    this.parameterLabel = params.options.parameter_label;

    if (this.api && this.parameterLabel) {
      const layer = await this.api.getLayer(this.layerName);

      // Sort by `wto_object` (Flussname)
      this.sortedStations = layer.data?.sort((a, b) => {
        const wtoComparison = a.WTO_OBJECT.localeCompare(b.WTO_OBJECT);
        if (wtoComparison !== 0) {
          return wtoComparison;
        }
        // Secondary sort by `station_no` - determines Down- and Upward direction
        const aId = parseInt(a.station_no, 10);
        const bId = parseInt(b.station_no, 10);
        // Parse so Int for comparison
        return aId - bId;
      });

      const stationIndex = this.sortedStations.findIndex(
        station => station.station_id === this.stationId,
      );

      if (stationIndex !== -1) {
        this.currStation = this.sortedStations[stationIndex];
        this.prevStation =
          stationIndex > 0 ? this.sortedStations[stationIndex - 1] : null;
        this.nextStation =
          stationIndex < this.sortedStations.length - 1
            ? this.sortedStations[stationIndex + 1]
            : null;
      }

      this.requestUpdate();
    }
  }

  /**
   * Updates the current route by replacing the station segments   *
   * @param {WiskiStation} to - Object with station_id and station_name or station_longname.
   */
  // eslint-disable-next-line class-methods-use-this
  switchStation(to: WiskiStation) {
    const currentPath = getCurrentPath();
    const newPath = buildStationUrl(currentPath, to);
    navigateTo(newPath);
  }
}
